import { Loader } from '@/ui/Loader'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ControlSheetTable } from './components/ControlSheetTable'

import styles from './ControlSheet.module.scss'
import { ControlSheetRoutePicker } from './components/ControlSheetRoutePicker'
import { useControlSheetTable } from '@/modules/ControlSheet/hooks/useControlSheetTable'
import { useDateStore } from '@/components/DateInput'
import { getAnnotationsPermission } from './getAnnotationsPermission'
import { useRetrieveAllControlSheetData } from '../hooks/useRetrieveAllControlSheetData'

export type AnnotationsPermission = 'view' | 'edit' | 'none'

export const ControlSheetPage = () => {
    const { date: isoDate } = useDateStore()
    const date = isoDate.split('T')[0]

    const lastProcessedRoute = useRef<string | null>(null)
    const [scrollToTimeRow, setScrollToTimeRow] = useState<string | null>(null)
    const [scrollToTripRow, setScrollToTripRow] = useState<string | null>(null)

    const [currentRouteIndex, setCurrentRouteIndex] = useState(0)

    const { controlSheets, routesWithPoints, allRoutes, allowEditAnnotations } = useRetrieveAllControlSheetData()

    const currentRoute = useMemo(() => {
        const route = routesWithPoints.data?.[currentRouteIndex]?.route
        return route
    }, [routesWithPoints, currentRouteIndex])

    const setTrip = useCallback((trip: string) => {
        setScrollToTripRow(trip)
    }, [])

    const annotationsPermission = getAnnotationsPermission(date, new Date(), allowEditAnnotations)

    const { table, timetable, mealRelief, refetch } = useControlSheetTable(
        currentRoute,
        annotationsPermission,
        controlSheets,
        setTrip
    )

    const rowModel = table.getRowModel()
    useEffect(() => {
        // Check if route changed, data is loaded, and we haven't processed this specific route yet
        if (currentRoute && currentRoute !== lastProcessedRoute.current && table.getRowModel().rows.length > 0) {
            const time = new Date().toLocaleTimeString('en-GB', {
                timeZone: 'Europe/London',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            })
            setScrollToTimeRow(time)

            // Update the last processed route
            lastProcessedRoute.current = currentRoute
            refetch()
        }
    }, [currentRoute, table, rowModel, refetch])

    if (!currentRoute) {
        return <Loader />
    }

    if (!allRoutes || !table) {
        return <Loader />
    }

    return (
        <div className={styles.container}>
            <div className={styles.frameContainer}>
                <div className={styles.routePickerContainer}>
                    <ControlSheetRoutePicker
                        routes={routesWithPoints.data || []}
                        selectedRoute={currentRouteIndex}
                        setSelectedRoute={setCurrentRouteIndex}
                    />
                    <div className={styles.timetableDetailsContainer}>
                        {timetable && (
                            <div className={styles.timetableContainer}>
                                {timetable.version} {timetable.day}
                            </div>
                        )}
                        {mealRelief && <div className={styles.mealReliefContainer}>Meal relief {mealRelief} min</div>}
                    </div>
                </div>
                <div className={styles.tableContainer}>
                    <ControlSheetTable
                        table={table}
                        scrollToTimeRow={scrollToTimeRow}
                        scrollToTripRow={scrollToTripRow}
                    />
                </div>
            </div>
        </div>
    )
}
